/**
 * FirmDashboard — the /firm landing page for firm staff.  Shows every
 * firm the caller belongs to, plus a summary of each firm's batches
 * (seat_count, redeemed count, available count, expiry).
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiV2 } from "@/lib/apiV2";
import { Card } from "@/components/ui/card";
import { Buildings, Prohibit, CaretRight } from "@phosphor-icons/react";

export default function FirmDashboard() {
  const me = useQuery({
    queryKey: ["firm-me"],
    queryFn: async () => (await apiV2.get("/firm/me")).data,
  });

  const batches = useQuery({
    queryKey: ["firm-batches"],
    queryFn: async () => (await apiV2.get("/firm/batches")).data,
    enabled: !!me.data,
  });

  if (me.isLoading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }

  if (me.isError) {
    return (
      <Card className="p-6" data-testid="firm-no-membership">
        <div className="flex items-start gap-3">
          <Prohibit size={22} className="text-amber-600 flex-shrink-0" />
          <div>
            <h2 className="font-medium">You're not linked to any firm</h2>
            <p className="text-sm text-slate-500 mt-1">
              Ask your firm's MedEasy admin to send you an invite. When they
              add your email, you'll get a magic-link to sign in and finish
              setup.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const memberships = me.data?.memberships || [];
  const batchesByFirm = new Map();
  for (const b of batches.data?.batches || []) {
    if (!batchesByFirm.has(b.firm_id)) batchesByFirm.set(b.firm_id, []);
    batchesByFirm.get(b.firm_id).push(b);
  }

  return (
    <div className="space-y-6" data-testid="firm-dashboard">
      <div>
        <h1 className="text-2xl font-semibold text-[#2C4C3B]">Firm dashboard</h1>
        <p className="text-sm text-slate-500">
          Your MedEasy seat batches and redemption status.
        </p>
      </div>

      {memberships.map((firm) => (
        <div key={firm.id} data-testid={`firm-section-${firm.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <Buildings size={20} className="text-[#2C4C3B]" />
            <h2 className="text-lg font-medium text-slate-900">
              {firm.name || firm.id}
            </h2>
            <span className="text-xs bg-slate-100 text-slate-600 rounded px-2 py-0.5">
              {firm.role}
            </span>
          </div>

          <Card className="divide-y divide-slate-100" data-testid={`firm-batches-${firm.id}`}>
            {(batchesByFirm.get(firm.id) || []).length === 0 ? (
              <div className="p-6 text-sm text-slate-500">
                No batches yet for this firm.
              </div>
            ) : (
              batchesByFirm.get(firm.id).map((b) => {
                const usedPct = Math.round((b.redeemed_count / b.seat_count) * 100);
                return (
                  <Link
                    key={b.id}
                    to={`/firm/batches/${b.id}`}
                    className="p-5 flex items-center justify-between hover:bg-slate-50 no-underline"
                    data-testid={`firm-batch-row-${b.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{b.label}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {b.redeemed_count} of {b.seat_count} seats used
                        {b.expires_at ? ` · expires ${b.expires_at}` : ""}
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden max-w-md">
                        <div
                          className="h-full bg-[#2C4C3B] transition-all"
                          style={{ width: `${usedPct}%` }}
                        />
                      </div>
                    </div>
                    <CaretRight size={18} className="text-slate-400 ml-4" />
                  </Link>
                );
              })
            )}
          </Card>
        </div>
      ))}
    </div>
  );
}
